import React, { useState, useEffect } from 'react';
import { onboardingAPI, documentsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Eye, CheckCircle2, XCircle, Loader2, FileText } from 'lucide-react';

const STATUS_VARIANT = {
  draft: 'secondary',
  pending_submission: 'secondary',
  pending_verification: 'default',
  verified: 'default',
  rejected: 'destructive',
  approved: 'default',
  active: 'default',
};

const Onboarding = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [hrNotes, setHrNotes] = useState('');
  const [acting, setActing] = useState(false);

  const fetchData = async () => {
    try {
      const res = await onboardingAPI.list();
      setRecords(res.data);
    } catch (e) {
      toast.error('Failed to load onboarding records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openDetail = async (record) => {
    setSelected(record);
    setHrNotes(record.hr_notes || '');
    try {
      const r = await documentsAPI.list(record.employee_id);
      setDocs(r.data);
    } catch { setDocs([]); }
  };

  const handleDecision = async (decision) => {
    if (!selected) return;
    setActing(true);
    try {
      await onboardingAPI.approve(selected.employee_id, { decision, hr_notes: hrNotes });
      toast.success(`Onboarding ${decision}d`);
      setSelected(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const verifyDoc = async (docId, decision) => {
    try {
      await documentsAPI.verify(docId, decision);
      toast.success(`Document ${decision}`);
      if (selected) {
        const r = await documentsAPI.list(selected.employee_id);
        setDocs(r.data);
      }
    } catch (e) {
      toast.error('Verification failed');
    }
  };

  const pending = records.filter(r => r.status === 'pending_verification');
  const drafts = records.filter(r => ['draft', 'pending_submission'].includes(r.status));
  const approved = records.filter(r => ['approved', 'verified', 'active'].includes(r.status));
  const rejected = records.filter(r => r.status === 'rejected');

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  const renderTable = (rows) => (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No records</TableCell></TableRow>
            ) : rows.map(r => (
              <TableRow key={r.id} data-testid={`onboarding-row-${r.id}`}>
                <TableCell className="font-medium">{r.employee_name}</TableCell>
                <TableCell>{r.employee_code}</TableCell>
                <TableCell>{r.department}</TableCell>
                <TableCell><Badge variant={STATUS_VARIANT[r.status] || 'secondary'}>{r.status?.replace(/_/g, ' ')}</Badge></TableCell>
                <TableCell>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—'}</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => openDetail(r)} data-testid={`view-onboarding-${r.id}`}>
                    <Eye size={14} className="mr-1" /> Review
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Onboarding Management</h1>
        <p className="text-muted-foreground">Review and approve new joiner onboarding submissions</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Pending Review</p><p className="text-2xl font-bold">{pending.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Drafts</p><p className="text-2xl font-bold">{drafts.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Approved</p><p className="text-2xl font-bold text-emerald-600">{approved.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Rejected</p><p className="text-2xl font-bold text-red-600">{rejected.length}</p></CardContent></Card>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="drafts">Drafts ({drafts.length})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({approved.length})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({rejected.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending">{renderTable(pending)}</TabsContent>
        <TabsContent value="drafts">{renderTable(drafts)}</TabsContent>
        <TabsContent value="approved">{renderTable(approved)}</TabsContent>
        <TabsContent value="rejected">{renderTable(rejected)}</TabsContent>
      </Tabs>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review: {selected?.employee_name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Email:</span> {selected.email}</div>
                <div><span className="text-muted-foreground">Code:</span> {selected.employee_code}</div>
                <div><span className="text-muted-foreground">Department:</span> {selected.department}</div>
                <div><span className="text-muted-foreground">Status:</span> {selected.status}</div>
              </div>

              {selected.personal_info && Object.keys(selected.personal_info).length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Personal Info</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm bg-muted/30 p-3 rounded">
                    {Object.entries(selected.personal_info).map(([k, v]) => (
                      <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, ' ')}:</span> {v || '—'}</div>
                    ))}
                  </div>
                </div>
              )}

              {selected.country_compliance && Object.keys(selected.country_compliance).length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Country Compliance</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm bg-muted/30 p-3 rounded">
                    {Object.entries(selected.country_compliance).map(([k, v]) => (
                      <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, ' ')}:</span> {v || '—'}</div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold mb-2">Documents ({docs.length})</h4>
                <div className="space-y-2">
                  {docs.length === 0 && <p className="text-sm text-muted-foreground">No documents uploaded</p>}
                  {docs.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex items-center gap-2">
                        <FileText size={16} />
                        <div>
                          <p className="text-sm font-medium">{d.file_name} <Badge variant="outline" className="ml-2">{d.category}</Badge></p>
                          <p className="text-xs text-muted-foreground">{d.status} · uploaded {new Date(d.uploaded_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {d.status !== 'verified' && <Button size="sm" variant="outline" onClick={() => verifyDoc(d.id, 'verify')}><CheckCircle2 size={14} /></Button>}
                        {d.status !== 'rejected' && <Button size="sm" variant="outline" onClick={() => verifyDoc(d.id, 'reject')}><XCircle size={14} /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">HR Notes</h4>
                <Textarea
                  value={hrNotes}
                  onChange={(e) => setHrNotes(e.target.value)}
                  placeholder="Add notes for the employee..."
                  data-testid="hr-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
            <Button variant="destructive" onClick={() => handleDecision('reject')} disabled={acting} data-testid="reject-onboarding-btn">
              {acting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<XCircle size={16} className="mr-1" /> Reject
            </Button>
            <Button onClick={() => handleDecision('approve')} disabled={acting} data-testid="approve-onboarding-btn">
              {acting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<CheckCircle2 size={16} className="mr-1" /> Approve & Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Onboarding;
